import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { MessageCircle, Send, Reply, Edit, Trash2, AtSign } from 'lucide-react';
import { TaskComment, Task, Project } from '@/types/product-manager';
import { getTaskComments, createTaskComment, updateTaskComment, deleteTaskComment, getTask, getProject } from '@/services/productManagerService';
import { productManagerEmailService } from '@/services/productManagerEmailService';
import { useAuth } from '@/context/AuthContext';
import { employeeNamesService } from '@/services/employeeNames';
import { getAllUserAppAccess } from '@/services/appAccess';
import { Timestamp } from 'firebase/firestore';
import { formatDistanceToNow } from 'date-fns';
import ImageUpload from './ImageUpload';
import ImageGallery from './ImageGallery';
import { LinkifiedText } from '@/utils/linkRenderer';
import { ImageUploadResult } from '@/services/productManagerImageService';
import { useModalClipboardPaste } from '@/hooks/useModalClipboardPaste';

interface CommentThreadProps {
  taskId: string;
  onCommentAdded?: () => void;
}

interface UserInfo {
  email: string;
  displayName: string;
}

const CommentThread: React.FC<CommentThreadProps> = ({ taskId, onCommentAdded }) => {
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [cursorPosition, setCursorPosition] = useState(0);
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [deletingComment, setDeletingComment] = useState<string | null>(null);
  const [task, setTask] = useState<Task | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [newCommentImages, setNewCommentImages] = useState<ImageUploadResult[]>([]);
  const [replyImages, setReplyImages] = useState<ImageUploadResult[]>([]);
  const [imageUploadError, setImageUploadError] = useState<string>('');
  
  const { userEmail } = useAuth();

  useEffect(() => {
    loadComments();
    loadUsers();
    loadTask();
  }, [taskId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadComments = async () => {
    try {
      setLoading(true);
      const commentsData = await getTaskComments(taskId);
      
      // Enhance comments with display names
      const enhancedComments = await Promise.all(
        commentsData.map(async (comment) => {
          const displayName = await employeeNamesService.getNameFromEmail(comment.userEmail);
          return {
            ...comment,
            userDisplayName: displayName || comment.userEmail
          };
        })
      );
      
      setComments(enhancedComments);
    } catch (error) {
      console.error('Error loading comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const allUsers = await getAllUserAppAccess();
      const productManagerUsers = allUsers.filter(user => 
        user.allowedApps.includes('product-manager')
      );
      
      const userInfos = await Promise.all(
        productManagerUsers.map(async (user) => {
          const displayName = await employeeNamesService.getNameFromEmail(user.email);
          return {
            email: user.email,
            displayName: displayName || user.email
          };
        })
      );
      
      setUsers(userInfos);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadTask = async () => {
    try {
      const taskData = await getTask(taskId);
      setTask(taskData);
      
      // Load project information if task is found
      if (taskData) {
        const projectData = await getProject(taskData.projectId);
        setProject(projectData);
      }
    } catch (error) {
      console.error('Error loading task:', error);
    }
  };

  const handleSubmitComment = async (content: string, parentCommentId?: string) => {
    if (!content.trim() || !userEmail) return;

    try {
      setSubmitting(true);
      
      // Extract mentions from content
      // Look for @mentions and try to match them against user names
      const mentions: string[] = [];
      
      // First, try to find exact matches by checking each user's name in the content
      for (const user of users) {
        const userName = user.displayName;
        const mentionPattern = new RegExp(`@${userName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(?=\\s|$)`, 'gi');
        if (mentionPattern.test(content)) {
          if (!mentions.includes(user.email)) {
            mentions.push(user.email);
          }
        }
      }
      
      // If no exact matches, try partial matching
      if (mentions.length === 0) {
        const mentionRegex = /@(\w+(?:\s+\w+)*)/g;
        let match: RegExpExecArray | null;
        
        while ((match = mentionRegex.exec(content)) !== null) {
          const mentionText = match[1].trim();
          
          const mentionedUser = users.find(user =>
            user.displayName.toLowerCase().includes(mentionText.toLowerCase()) ||
            user.email.toLowerCase().includes(mentionText.toLowerCase())
          );
          
          if (mentionedUser && !mentions.includes(mentionedUser.email)) {
            mentions.push(mentionedUser.email);
          }
        }
      }

      const comment: Omit<TaskComment, 'id'> = {
        taskId,
        userId: userEmail,
        userEmail,
        content: content.trim(),
        createdAt: Timestamp.now() as any,
        ...(mentions.length > 0 && { mentions })
      };

      // Only add parentCommentId if it exists and is not undefined
      if (parentCommentId) {
        comment.parentCommentId = parentCommentId;
      }

      // Add images if any
      const imagesToAdd = parentCommentId ? replyImages : newCommentImages;
      if (imagesToAdd.length > 0) {
        comment.images = imagesToAdd.map(img => img.url);
      }

      await createTaskComment(comment);
      
      // Send email notifications
      if (task) {
        try {
          // 1. Send mention notifications to mentioned users
          if (mentions.length > 0) {
            const uniqueMentions = [...new Set(mentions)]; // Remove duplicates
            for (const mentionedEmail of uniqueMentions) {
              await productManagerEmailService.sendMentionNotification(
                mentionedEmail,
                taskId,
                task.title,
                project?.name || 'Unknown Project',
                userEmail,
                content.trim()
              );
            }
          }
          
          // 2. Send comment notification to task assignee (if different from commenter and not already mentioned)
          if (task.assignedTo && task.assignedTo !== userEmail && !mentions.includes(task.assignedTo)) {
            await productManagerEmailService.sendCommentAddedNotification(
              task.assignedTo,
              taskId,
              task.title,
              project?.name || 'Unknown Project',
              userEmail
            );
          }
        } catch (emailError) {
          console.error('Error sending email notifications:', emailError);
          // Don't fail comment creation if email fails
        }
      }
      
      if (parentCommentId) {
        setReplyingTo(null);
        setReplyContent('');
        setReplyImages([]);
      } else {
        setNewComment('');
        setNewCommentImages([]);
      }
      setImageUploadError('');
      
      await loadComments();
      onCommentAdded?.();
    } catch (error) {
      console.error('Error submitting comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleMentionInput = (value: string, cursorPos: number) => {
    const beforeCursor = value.substring(0, cursorPos);
    const atIndex = beforeCursor.lastIndexOf('@');
    
    if (atIndex !== -1 && atIndex === beforeCursor.length - 1) {
      // Just typed @
      setShowMentionSuggestions(true);
      setMentionQuery('');
      setCursorPosition(cursorPos);
    } else if (atIndex !== -1 && beforeCursor.substring(atIndex + 1).indexOf(' ') === -1) {
      // Typing after @
      const query = beforeCursor.substring(atIndex + 1);
      setShowMentionSuggestions(true);
      setMentionQuery(query);
      setCursorPosition(cursorPos);
    } else {
      setShowMentionSuggestions(false);
    }
  };

  const insertMention = (user: UserInfo, isReply: boolean = false) => {
    const setter = isReply ? setReplyContent : setNewComment;
    const currentValue = isReply ? replyContent : newComment;
    
    const beforeCursor = currentValue.substring(0, cursorPosition);
    const afterCursor = currentValue.substring(cursorPosition);
    const atIndex = beforeCursor.lastIndexOf('@');
    
    const newValue =
      beforeCursor.substring(0, atIndex) +
      `@${user.displayName} ` +
      afterCursor;
    
    setter(newValue);
    setShowMentionSuggestions(false);
  };

  const handleEditComment = async (commentId: string, newContent: string) => {
    try {
      setSubmitting(true);
      await updateTaskComment(commentId, {
        content: newContent,
        updatedAt: Timestamp.now() as any
      });
      setEditingComment(null);
      setEditContent('');
      await loadComments();
    } catch (error) {
      console.error('Error updating comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    try {
      setSubmitting(true);
      await deleteTaskComment(commentId);
      setDeletingComment(null);
      await loadComments();
    } catch (error) {
      console.error('Error deleting comment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const startEditComment = (comment: TaskComment & { userDisplayName?: string }) => {
    setEditingComment(comment.id);
    setEditContent(comment.content);
  };

  const cancelEditComment = () => {
    setEditingComment(null);
    setEditContent('');
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatTimestamp = (timestamp: any) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return formatDistanceToNow(date, { addSuffix: true });
  };

  const handleNewCommentImagesUploaded = (images: ImageUploadResult[]) => {
    setNewCommentImages(prev => [...prev, ...images]);
    setImageUploadError('');
  };

  const handleReplyImagesUploaded = (images: ImageUploadResult[]) => {
    setReplyImages(prev => [...prev, ...images]);
    setImageUploadError('');
  };

  const handleImageUploadError = (error: string) => {
    setImageUploadError(error);
  };

  const handleNewCommentImageDeleted = (imageUrl: string) => {
    setNewCommentImages(prev => prev.filter(img => img.url !== imageUrl));
  };

  const handleReplyImageDeleted = (imageUrl: string) => {
    setReplyImages(prev => prev.filter(img => img.url !== imageUrl));
  };

  // Handle clipboard paste - convert File[] to ImageUploadResult[] format
  const handleClipboardPaste = async (files: File[]) => {
    try {
      // Import the upload function
      const { uploadCommentImage } = await import('@/services/productManagerImageService');
      
      // Upload each file and convert to ImageUploadResult format
      const uploadPromises = files.map(async (file) => {
        const result = await uploadCommentImage('temp', file);
        return result;
      });
      
      const uploadResults = await Promise.all(uploadPromises);
      handleNewCommentImagesUploaded(uploadResults);
    } catch (error) {
      console.error('Error uploading clipboard images:', error);
      handleImageUploadError('Failed to upload clipboard images');
    }
  };

  // Modal-level clipboard paste functionality
  const { modalRef } = useModalClipboardPaste({
    onImagesPasted: handleClipboardPaste,
    onError: handleImageUploadError,
    maxImages: 3,
    disabled: submitting,
    enablePaste: true
  });

  const renderComment = (comment: TaskComment & { userDisplayName?: string }, isReply = false) => (
    <div key={comment.id} className={`${isReply ? 'ml-8 mt-2' : 'mb-4'}`}>
      <div className="flex space-x-3">
        <Avatar className="h-8 w-8">
          <AvatarFallback className="text-xs">
            {getInitials(comment.userDisplayName || comment.userEmail)}
          </AvatarFallback>
        </Avatar>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {comment.userDisplayName || comment.userEmail}
            </span>
            <span className="text-xs text-gray-500">
              {formatTimestamp(comment.createdAt)}
            </span>
            {comment.mentions && comment.mentions.length > 0 && (
              <Badge variant="secondary" className="text-xs">
                <AtSign className="h-3 w-3 mr-1" />
                {comment.mentions.length}
              </Badge>
            )}
          </div>
          
          {editingComment === comment.id ? (
            <div className="mt-2 space-y-2">
              <Textarea
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                className="min-h-[60px] resize-none"
                disabled={submitting}
              />
              <div className="flex space-x-2">
                <Button
                  size="sm"
                  onClick={() => handleEditComment(comment.id, editContent)}
                  disabled={!editContent.trim() || submitting}
                  className="bg-violet-600 hover:bg-violet-700"
                >
                  Save
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={cancelEditComment}
                  disabled={submitting}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="mt-1 text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                <LinkifiedText text={comment.content} />
              </div>
              
              {/* Comment Images */}
              {comment.images && comment.images.length > 0 && (
                <div className="mt-3">
                  <ImageGallery
                    images={comment.images}
                    canDelete={false}
                    showDownload={true}
                  />
                </div>
              )}
              
              <div className="mt-2 flex items-center space-x-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setReplyingTo(comment.id)}
                  className="text-xs h-6 px-2"
                >
                  <Reply className="h-3 w-3 mr-1" />
                  Reply
                </Button>
                
                {comment.userEmail === userEmail && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs h-6 px-2"
                      onClick={() => startEditComment(comment)}
                    >
                      <Edit className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-xs h-6 px-2 text-red-600"
                      onClick={() => setDeletingComment(comment.id)}
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Delete
                    </Button>
                  </>
                )}
              </div>
            </>
          )}
          
          {replyingTo === comment.id && (
            <div className="mt-3 space-y-2">
              <div className="relative">
                <Textarea
                  placeholder={`Reply to ${comment.userDisplayName || comment.userEmail}...`}
                  value={replyContent}
                  onChange={(e) => {
                    setReplyContent(e.target.value);
                    handleMentionInput(e.target.value, e.target.selectionStart || 0);
                  }}
                  className="min-h-[80px] resize-none"
                  disabled={submitting}
                />
                
                {showMentionSuggestions && (
                  <div className="absolute top-full left-0 right-0 z-10 mt-1 bg-white dark:bg-gray-800 border rounded-md shadow-lg max-h-40 overflow-y-auto">
                    {users
                      .filter(user => 
                        user.displayName.toLowerCase().includes(mentionQuery.toLowerCase()) ||
                        user.email.toLowerCase().includes(mentionQuery.toLowerCase())
                      )
                      .slice(0, 5)
                      .map(user => (
                        <button
                          key={user.email}
                          onClick={() => insertMention(user, true)}
                          className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                        >
                          <Avatar className="h-6 w-6">
                            <AvatarFallback className="text-xs">
                              {getInitials(user.displayName)}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <div className="text-sm font-medium">{user.displayName}</div>
                            <div className="text-xs text-gray-500">{user.email}</div>
                          </div>
                        </button>
                      ))
                    }
                  </div>
                )}
              </div>
              
              <div className="flex space-x-2">
                <Button
                  size="sm"
                  onClick={() => handleSubmitComment(replyContent, comment.id)}
                  disabled={!replyContent.trim() || submitting}
                  className="bg-violet-600 hover:bg-violet-700"
                >
                  <Send className="h-3 w-3 mr-1" />
                  Reply
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setReplyingTo(null);
                    setReplyContent('');
                  }}
                  disabled={submitting}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Render replies */}
      {comments
        .filter(reply => reply.parentCommentId === comment.id)
        .map(reply => renderComment(reply, true))
      }
    </div>
  );

  const topLevelComments = comments.filter(comment => !comment.parentCommentId);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <MessageCircle className="h-5 w-5 mr-2" />
            Comments
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex space-x-3 animate-pulse">
                <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card ref={modalRef}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center">
            <MessageCircle className="h-5 w-5 mr-2" />
            Comments
            {comments.length > 0 && (
              <Badge variant="secondary" className="ml-2">
                {comments.length}
              </Badge>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* New Comment Form */}
        <div className="space-y-3">
          <div className="relative">
            <Textarea
              placeholder="Add a comment... Use @name to mention someone"
              value={newComment}
              onChange={(e) => {
                setNewComment(e.target.value);
                handleMentionInput(e.target.value, e.target.selectionStart || 0);
              }}
              className="min-h-[100px] resize-none"
              disabled={submitting}
            />
            
            {showMentionSuggestions && !replyingTo && (
              <div className="absolute top-full left-0 right-0 z-10 mt-1 bg-white dark:bg-gray-800 border rounded-md shadow-lg max-h-40 overflow-y-auto">
                {users
                  .filter(user => 
                    user.displayName.toLowerCase().includes(mentionQuery.toLowerCase()) ||
                    user.email.toLowerCase().includes(mentionQuery.toLowerCase())
                  )
                  .slice(0, 5)
                  .map(user => (
                    <button
                      key={user.email}
                      onClick={() => insertMention(user)}
                      className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2"
                    >
                      <Avatar className="h-6 w-6">
                        <AvatarFallback className="text-xs">
                          {getInitials(user.displayName)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="text-sm font-medium">{user.displayName}</div>
                        <div className="text-xs text-gray-500">{user.email}</div>
                      </div>
                    </button>
                  ))
                }
              </div>
            )}
          </div>

          {/* Image Upload for New Comment */}
          <ImageUpload
            entityType="comment"
            entityId="temp" // Will be replaced with actual comment ID after creation
            onImagesUploaded={handleNewCommentImagesUploaded}
            onError={handleImageUploadError}
            disabled={submitting}
            maxImages={3}
            enableClipboardPaste={true}
          />
          
          {imageUploadError && (
            <p className="text-sm text-red-500">{imageUploadError}</p>
          )}
          
          {newCommentImages.length > 0 && (
            <ImageGallery
              images={newCommentImages.map(img => img.url)}
              onImageDeleted={handleNewCommentImageDeleted}
              canDelete={true}
              showDownload={false}
              deleteFromStorage={false}
            />
          )}
          
          <div className="flex justify-between items-center">
            <div className="text-xs text-gray-500">
              Tip: Use @name to mention team members
            </div>
            <Button
              onClick={() => handleSubmitComment(newComment)}
              disabled={(!newComment.trim() && newCommentImages.length === 0) || submitting}
              className="bg-violet-600 hover:bg-violet-700"
            >
              <Send className="h-4 w-4 mr-2" />
              {submitting ? 'Posting...' : 'Post Comment'}
            </Button>
          </div>
        </div>
        
        {/* Comments List */}
        {topLevelComments.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <MessageCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No comments yet. Be the first to comment!</p>
          </div>
        ) : (
          <div className="space-y-6">
            {topLevelComments.map(comment => renderComment(comment))}
          </div>
        )}
      </CardContent>
      
      {/* Delete Confirmation Dialog */}
      {deletingComment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-2">Delete Comment</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Are you sure you want to delete this comment? This action cannot be undone.
            </p>
            <div className="flex space-x-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setDeletingComment(null)}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => handleDeleteComment(deletingComment)}
                disabled={submitting}
              >
                {submitting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default CommentThread;